import { ReadonlyURLSearchParams } from 'next/navigation';
import { SafeParseReturnType } from 'zod/lib/types';
import { Money, Product } from './bigcommerce/types';
import { BASE_URL } from './constants';
import { BCConsignment, BCDownloads, BCGiftCerticicates, BCLineItem, BCShipping } from './rest-service/types';

export * from './cookies';

export function absoluteUrl(urlString: string) {
  if (urlString.startsWith('http')) {
    return urlString;
  }

  const url = new URL(urlString, BASE_URL);
  return url.toString();
}

export function relativeUrl(urlString: string) {
  if (urlString.startsWith('/')) {
    return urlString;
  }

  const url = new URL(urlString);
  const queryString = url.searchParams.toString();
  if (queryString) {
    return `${url.pathname}?${url.searchParams.toString()}`;
  }
  return url.pathname;
}

export const createUrl = (pathname: string, params: URLSearchParams | ReadonlyURLSearchParams) => {
  const paramsString = params.toString();
  const queryString = `${paramsString.length ? '?' : ''}${paramsString}`;

  return `${pathname}${queryString}`;
};

export const ensureStartsWith = (stringToCheck: string, startsWith: string) =>
  stringToCheck.startsWith(startsWith) ? stringToCheck : `${startsWith}${stringToCheck}`;

export const validateEnvironmentVariables = () => {
  const requiredEnvironmentVariables = [
    'BIGCOMMERCE_CANONICAL_STORE_DOMAIN',
    'BIGCOMMERCE_ACCESS_TOKEN',
    'BIGCOMMERCE_CUSTOMER_IMPERSONATION_TOKEN',
    'BIGCOMMERCE_STORE_HASH',
    'BIGCOMMERCE_CLIENT_ID',
    'BIGCOMMERCE_CLIENT_SECRET'
  ];
  const missingEnvironmentVariables = [] as string[];

  requiredEnvironmentVariables.forEach((envVar) => {
    if (!process.env[envVar]) {
      missingEnvironmentVariables.push(envVar);
    }
  });

  if (missingEnvironmentVariables.length) {
    throw new Error(
      `The following environment variables are missing. Your site will not work without them. \n\n${missingEnvironmentVariables.join(
        '\n'
      )}\n`
    );
  }
};

export const hasSalePrice = <T extends { salePrice?: Money; }>(productOrVariant: T) => productOrVariant.salePrice !== undefined && productOrVariant.salePrice.amount !== '0';
export const getSalePrice = <T extends { salePrice?: Money; basePrice: Money; }>(productOrVariant: T): Money => hasSalePrice(productOrVariant) ? productOrVariant.salePrice! : productOrVariant.basePrice;

export const isDevelopment = (baseUrl?: string) => process.env.NODE_ENV === "development" || /(?:localhost|127.0.0.1)/.test(baseUrl ?? BASE_URL);
export const isSB = (baseUrl?: string) => envId(baseUrl) === 'sb';
export const isQA = (baseUrl?: string) => envId(baseUrl) === 'qa';
export const isUAT = (baseUrl?: string) => envId(baseUrl) === 'uat';
export const envId = (baseUrl?: string) => (baseUrl ?? BASE_URL).match(/ecommerce\.([a-z]*)\.4patriots\.net/)?.at(1);

export function zodErrorMessage<Input, Output>(result: SafeParseReturnType<Input, Output>) {
  if (result && !result.success) {
    const error = result.error?.errors?.at(0);
    const message = error?.message ?? 'Invalid Request';
    const path = error?.path
    return `Invalid Request - ${message} ${path}`;
  }
  return 'Invalid Request'
}

export function hasPaymentPlan(product?: Product, variantSku?: string) {
  if (!product || !variantSku) {
    return false;
  }

  const paymentMetafield = product.variants.find(v => v.sku === variantSku)?.paymentMetafields?.find((m) => m.key === 'payment-type');
  return !!(paymentMetafield && ['payment-plan', 'subscription'].includes(paymentMetafield.value.toString()));
}

export const tryParseMoney = (money?: string) => {
  const cleanString = money?.replace(/[^0-9.-]+/g, '');
  return cleanString ? parseFloat(cleanString) : undefined;
};

export function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const extractConsigments = (array?: BCConsignment[]): { shipping: BCShipping[]; downloads: BCDownloads[]; gift_certificates: BCGiftCerticicates[] } => {
  if (array && Array.isArray(array)) {
    const shipping = array.flatMap(x => x.shipping).filter((x) => x !== undefined) as BCShipping[];
    const downloads = array.flatMap(x => x.downloads).filter((x) => x !== undefined) as BCDownloads[];
    const gift_certificates = array.flatMap(x => x.email?.gift_certificates).filter((x) => x !== undefined) as BCGiftCerticicates[];
    return { shipping, downloads, gift_certificates };
  }
  return { shipping: [], downloads: [], gift_certificates: [] };
};

export const extractLineItems = (consigments?: { line_items?: BCLineItem[] }[]) => {
  const lineItems: BCLineItem[] = [];
  const dirtyLineItems = consigments?.flatMap(c => c.line_items ?? []) ?? [];
  for (const line of dirtyLineItems) {
    if ('id' in line && line.id !== undefined)
      lineItems.push(line);
  }
  return lineItems;
};